// @flow

import type { ExperienceMap } from 'app/common/lib/react/experiences/experienceUtils';
import type { VersionTuple } from 'node/lib/RequestUtils';
import ValueUtil from 'app/common/lib/ValueUtil';
import type { AuthUserAttributes, UnauthUserAttributes } from 'app/common/lib/User';

type ExperimentGroupMap = {
  [expName: string]: string,
};

type PartialContext = {
  request_debug: Object,
  _uid?: mixed,

  // logging
  analysis_ua: Object,
  request_identifier: string,
  root_request_identifier: ?string,
  parent_request_identifier: ?string,
  full_path: string,
  real_ip: string,

  active_experiments: ExperimentGroupMap,
  android_deep_link: ?string,
  app_shell: boolean,
  app_type: ?number,
  app_version: string,
  browser_locale: ?string,
  browser_name: string,
  browser_type: string,
  browser_version: string,
  cookies: { [key: string]: string },
  country: string,
  current_url: string,
  deep_link_action: ?string,
  deep_link_default: string,
  deep_link_tracking?: mixed,
  deep_link: string,
  enabled_advertiser_countries: string[],
  error_template_path?: mixed,
  event_log_info: Object,
  experiment_hash: string,
  facebook_token: ?string,
  from_open_in_app_click: ?string,
  giftwrap_holdout?: mixed,
  has_sterling_access: boolean,
  has_sterling_on_steroids_write_access: boolean,
  invite_sender_experiments: any,
  has_ads_api_access: boolean,
  http_referrer: string,
  invite_code: ?string,
  ios_deep_link: ?string,
  is_advertiser_cap_whitelisted: boolean,
  is_amp: boolean,
  is_bot: 'true' | 'false',
  is_carousel_campaign_whitelisted: boolean,
  is_internal_ip: boolean,
  is_full_page: boolean,
  is_managed_advertiser: boolean,
  is_mdl_campaign_type_whitelisted: boolean,
  is_mobile_agent: boolean,
  is_one_tap_default_on_whitelisted: boolean,
  is_one_tap_unmigrated_advertiser: boolean,
  is_shop_the_pin_campaign_whitelisted: boolean,
  is_retina: boolean,
  is_sterling_on_steroids: boolean,
  is_tablet_agent: boolean,
  language: string,
  locale: string,
  mdl_scheme_whitelist: Array<string>,
  mock_date?: string,
  origin: string,
  path: string,
  pure_react: boolean,
  react_only: ?boolean,
  referrer: ?string,
  seo_experiments: ExperimentGroupMap,
  site_const: Object,
  site_type: number,
  should_use_sterling_token: boolean,
  sterling_on_steroids_access_token: string,
  sterling_on_steroids_ldap: string,
  /* eslint-disable no-use-before-define */
  test_context?: ClientContext,
  /* eslint-enable no-use-before-define */
  triggerable_experiments: ExperimentGroupMap,
  triggerable_experiments_hash: string,
  unauth_id: string,
  user_agent_can_use_native_app: boolean,
  user_agent_is_ios_9_or_above: boolean,
  user_agent_platform: string,
  user_agent_platform_version: null | VersionTuple,
  user_agent: string,
  utm_campaign: ?string,
  utm_medium: ?string,
  utm_source: ?string,
  utm_term: ?string,
  utm_pai: ?string,
  visible_url: string,
  PAGE_LOAD_REQUEST_IDENTIFIER?: string,
  /* stage and appVersion are sent down as the initialPageContext  */
  stage: ?string,
  appVersion: ?string,
  page_type: ?string,
};

// $FlowFixMe: new errors found from flow 0.65 upgrade
export type AuthContext = PartialContext & {
  is_authenticated: true,
  placed_experiences: ExperienceMap,
  user: AuthUserAttributes,
};

export type UnauthContext = PartialContext & {
  is_authenticated: false,
  advertiser: null,
  user: UnauthUserAttributes,
};

export type AdvertiserContextType = {
  billing_type: number,
  country: number,
  created_time: number,
  currency: number,
  currency_code: string,
  id: string,
  inactive_advertisers?: Array<Object>,
  name: string,
  owner_user_id: string,
  bidded_cpm_custom_bid_floor: number,
  merchant_id?: string,
  landing_page_view_enabled?: boolean,
  actions?: Array<number>,
};

export type PartnerContext = PartialContext & {
  is_authenticated: true,
  placed_experiences: ExperienceMap,
  advertiser: AdvertiserContextType,
  visible_url: string,
  user: AuthUserAttributes,
};

export type ClientContext = AuthContext | UnauthContext | PartnerContext;

/**
 * Parses the browser's document cookie value and returns a dictionary
 */
function _get_browser_cookies(): { [key: string]: string } {
  const obj = {};

  // Check for existence of document and document.cookie. If null, return an empty object.
  if (!document || !document.cookie) {
    return {};
  }

  const parsed = document.cookie.split(';');
  for (let i = 0; i < parsed.length; i += 1) {
    const el = parsed[i].split('=');
    obj[el[0].trim()] = el[1];
  }
  return obj;
}

const context = {
  instance: {},
  clientOnlyInstance: {},
  initialPageContext: {},
  set(newContext: ClientContext) {
    this.instance = newContext;
    ValueUtil.deepExtend(this.instance, this.clientOnlyInstance);

    // Update the cookies found in the context
    this.instance.cookies = _get_browser_cookies();
  },

  setInitialPageContext(initialPageContext: ClientContext) {
    this.initialPageContext = initialPageContext;
  },

  updateClientOnlyValue(key: string, value: any) {
    this.instance[key] = value;
    this.clientOnlyInstance[key] = value;
  },
};

type Context = {
  instance: ClientContext,
  clientOnlyInstance: { [key: string]: mixed },
  initialPageContext: ClientContext,
  set(c: ClientContext): void,
  setInitialPageContext(c: ClientContext): void,
  updateClientOnlyValue(key: string, value: mixed): void,
};

export default ((context: any): Context);
